import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../middleware/ThemeContext";
import styled from "styled-components";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import TickIcon from "../../assets/icons/TickIcon";
import { LoadingSpinner } from "../../features/ui/LoadingSpinner";
import { Theme } from "../../utils/themes";
import FillEditIcon from "../../assets/icons/FillEditIcon";
import { getInitial } from "../../utils/helper";
import AddOrg from "../../assets/icons/controlBar/AddOrg";
import { useToast } from "../toast/ToastContext";

interface AvatarProps {
  name: string;
  imageUrl?: string;
  size?: number;
  rounded?: boolean;
  className?: string;
  border?: string;
  enableUpload?: boolean; // 👈 make upload optional
  onUploadSuccess?: (url: string) => void; // callback when uploaded
  onUpload?: (file: File) => Promise<string>;
  handleUpdate?: (imageKey: string) => void;
  loading?: boolean;
  type?: string;
}

const Avatar: React.FC<AvatarProps> = ({
  name,
  imageUrl,
  size = 40,
  rounded = true,
  className = "",
  border = "none",
  enableUpload = false,
  onUpload,
  handleUpdate,
  loading,
  type
}) => {

  const [localImage, setLocalImage] = useState<string | null>(null);
  const [uploadedKey, setUploadedKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { theme } = useTheme();
  const initial = getInitial(name);
  const [hovered, setHovered] = useState(false);
  const handleFileSelect = () => fileInputRef.current?.click();
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [fullImageLoaded, setFullImageLoaded] = useState(false);

  const showToast = useToast();
  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/jpe",
    "image/png",
    "image/gif",
    "image/bmp"
  ];

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB


  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;

    if (file.size > MAX_FILE_SIZE) {
      showToast("Error", "File size must be less than 5MB", "error");
      return;
    }

    // -------- FILE TYPE CHECK -------- //
    if (!ALLOWED_TYPES.includes(file.type)) {
      showToast(
        "Error",
        "Invalid file type. Allowed types: JPG, JPEG, JPE, PNG, GIF, BMP",
        "error"
      );
      return;
    }

    const localUrl = URL.createObjectURL(file);

    setLocalImage(localUrl);


    try {
      // 🔥 call parent-provided upload handler
      const key = await onUpload(file);
      setUploadedKey(key);
    } catch (error) {
      console.error("Upload failed", error);
    }
  };

  // const handleRemoveImage = () => {
  //   setLocalImage(null); // preview clear karo

  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = ""; // file input reset karo
  //   }
  // };

  const handleRemoveImage = () => {
    setLocalImage(null);
    setUploadedKey(null);
  };

  const handleSave = () => {
    if (!uploadedKey || !handleUpdate) return;

    handleUpdate(uploadedKey); // 🔥 parent API call
    setLocalImage(null);
    setUploadedKey(null);


  };

  useEffect(() => {
    if (imageUrl) setImageLoadFailed(false); setFullImageLoaded(false);
  }, [imageUrl]);



  const handleImageError = () => {
    setImageLoadFailed(true);
  };

  const shouldShowFallbackImage = type === 'organization' && (imageLoadFailed || !imageUrl) && !localImage;

  return (
    <div
      className={`d-flex align-items-center justify-content-center bg-white text-primary fw-bold position-relative cusror-pointer ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: rounded ? "50%" : "0.25rem",
        // overflow: "hidden",
        fontSize: size / 2,
        textTransform: "uppercase",
        border: border,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {imageUrl || localImage ? (
        // <img
        //   src={localImage || imageUrl}
        //   alt={name}
        //   loading="lazy"
        //   decoding="async"
        //   style={{
        //     width: "100%",
        //     height: "100%",
        //     objectFit: "cover",
        //     borderRadius: "50%",
        //     filter: fullImageLoaded ? "none" : "blur(10px)",
        //     transition: "filter 0.3s ease",
        //   }}
        //   onLoad={() => setFullImageLoaded(true)}

        //   onError={handleImageError}
        // />
        <img
          src={localImage || imageUrl}
          alt={name}
          loading="lazy"
          decoding="async"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
            filter: !localImage && !fullImageLoaded ? "blur(10px)" : "none",
            transition: "filter 0.3s ease",
          }}
          onLoad={() => setFullImageLoaded(true)}
          onError={handleImageError}
        />
      ) : shouldShowFallbackImage ? (
        // 👇 Organization ke liye generic icon ya blank
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          borderRadius: '50%',
        }}>
          {/* Optional: Organization icon */}
          <AddOrg size={size * 0.5} color="#999" />
        </div>
      ) : (
        <span>{initial}</span>
      )}

      {enableUpload && (
        <>
          <StyledDiv
            theme={theme}
            localImage={!!localImage}
            size={size}
            style={{
              opacity: hovered || localImage || loading ? 1 : 0, // show on hover or if preview exists
              transition: "opacity 0.2s",
            }}
          >
            {loading ? (
              <LoadingSpinner size="20px" color="black" />
            ) : localImage ? (
              <>
                <DeleteIcon
                  color="black"
                  size={20}
                  onClick={handleRemoveImage}
                  className="cursor-pointer"
                />
                <TickIcon
                  color="black"
                  size={20}
                  onClick={handleSave}
                  className="cursor-pointer"

                />
              </>
            ) : (
              <FillEditIcon
                onClick={handleFileSelect}
                color="black"
                size={24}
                className="cursor-pointer"
              />
            )}
          </StyledDiv>

          <input
            type="file"
            accept=".jpg,.jpeg,.jpe,.png,.gif,.bmp"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              handleFileChange(e);
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }}
          />
        </>
      )}
    </div>
  );
};



const hexToRgb = (hex: string) => {
  const sanitizedHex = hex.replace('#', '');
  const bigint = parseInt(sanitizedHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

const StyledDiv = styled.div<{ localImage: boolean; theme: Theme; size: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ localImage }) => (localImage ? '8px' : '4px')};
  
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
      background: ${({ theme }) => `rgba(${hexToRgb(theme.CreateFormTheme.colors.inputBorders)}, 0.7)`}; 

  border-radius:50%;
  
  z-index: 999;
  opacity: 0;
  transition: opacity 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

export default Avatar;
